import LayersIcon from "@mui/icons-material/LayersOutlined";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import SchoolIcon from "@mui/icons-material/SchoolOutlined";
import { useTranslation } from "react-i18next";
import { assets } from "@/lib/assets";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { PageBanner } from "@/components/PageBanner";
import { cardBase, container, eyebrow } from "@/lib/ui";

const advIcons = [LayersIcon, TrackChangesIcon, GroupsIcon, SchoolIcon];

const partners = [
  { key: "zxx", logo: assets.partnerZxx, url: "http://www.zxxjt.com/gongcheng/" },
  { key: "runda", logo: assets.partnerRunda, url: "https://www.rundapv.com/cn/" },
  { key: "enxi", logo: assets.partnerEnxi, url: "https://www.evirthfood.com/" },
  { key: "mps", logo: assets.partnerMps, url: "https://www.trimps.net.cn/" },
  { key: "ecnu", logo: assets.partnerEcnu, url: "https://www.ecnu.edu.cn/" },
] as const;

export function About() {
  const { t } = useTranslation();
  const advItems = t("about:advantages", { returnObjects: true }) as unknown as {
    title: string;
    desc: string;
  }[];
  const partnerNames = t("about:partners", { returnObjects: true }) as unknown as Record<string, string>;

  return (
    <>
      {/* 3.2 Banner */}
      <PageBanner
        eyebrow={t("about:banner.eyebrow")}
        title={t("about:banner.title")}
        subtitle={t("about:banner.subtitle")}
      />

      {/* 3.3 Intro */}
      <section className="bg-bg py-24 md:py-32">
        <div className={`${container} grid items-center gap-12 lg:grid-cols-2 lg:gap-20`}>
          <Reveal>
            <p className={eyebrow}>{t("about:intro.eyebrow")}</p>
            <h2 className="mt-4 font-display text-3xl font-bold leading-tight md:text-4xl">
              {t("about:intro.heading")}
            </h2>
            <p className="mt-6 text-fg-muted">{t("about:intro.p1")}</p>
            <p className="mt-4 text-fg-muted">{t("about:intro.p2")}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="pointer-events-none absolute -inset-5 rounded-[2rem] bg-[radial-gradient(circle_at_50%_35%,rgba(1,120,254,0.22),transparent_70%)] blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl border border-hairline shadow-2xl">
                <img src={assets.fullStackCollab} alt={t("about:intro.imageAlt")} className="w-full" />
                <div className="absolute inset-0 ring-1 ring-inset ring-brand/20" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3.4 Core advantages */}
      <section className="bg-surface py-24 md:py-32">
        <div className={container}>
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className={eyebrow}>{t("about:advantagesSection.eyebrow")}</p>
            <h2 className="mt-4 font-display text-3xl font-bold md:text-4xl">
              {t("about:advantagesSection.heading")}
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {advItems.map((adv, i) => {
              const Icon = advIcons[i]!;
              return (
                <Reveal key={adv.title} delay={i * 0.08}>
                  <div className={`group relative h-full overflow-hidden ${cardBase} p-7 hover:-translate-y-1.5 hover:border-brand/50 hover:shadow-[0_24px_60px_-24px_rgba(1,120,254,0.5)]`}>
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(1,120,254,0.14),transparent_70%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <span
                      aria-hidden
                      className="pointer-events-none absolute right-3 top-1 select-none font-display text-6xl font-bold leading-none text-fg/[0.04]"
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="relative flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-brand/25 to-brand-cyan/10 text-brand ring-1 ring-inset ring-brand/20 transition-transform duration-300 group-hover:scale-110">
                      <Icon style={{ fontSize: 26 }} />
                    </div>
                    <h3 className="relative mt-5 text-lg font-semibold">{adv.title}</h3>
                    <p className="relative mt-2 text-sm leading-relaxed text-fg-muted">{adv.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3.5 Partners / academic network */}
      <section className="bg-bg py-24 md:py-32">
        <div className={container}>
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className={eyebrow}>{t("about:partnersSection.eyebrow")}</p>
            <h2 className="mt-4 font-display text-3xl font-bold md:text-4xl">
              {t("about:partnersSection.heading")}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {partners.map((partner, i) => (
              <Reveal key={partner.key} delay={i * 0.06}>
                <a
                  href={partner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={partnerNames[partner.key]}
                  className={`group flex h-28 items-center justify-center ${cardBase} p-6 hover:-translate-y-1.5 hover:border-brand/40 hover:shadow-[0_20px_50px_-24px_rgba(1,120,254,0.4)]`}
                >
                  <img
                    src={partner.logo}
                    alt={partnerNames[partner.key]}
                    className="max-h-14 w-auto max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaSection variant="about" />
    </>
  );
}
